import {
  useCallback, useEffect, useState, useMemo
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  setTaskPending,
  setTaskFulfilled,
  addErrorMessageAction,
  clearSurveySchemaPartOptionsAction,
  updateSurveySchemaPartOptionsAction,
  getOrCheckSurveySchemaPartCodeAction
} from '../../../actions';

import {
  getSurveySchemaPartUsage,
  getExtraSurveyPartUsage,
  getSurveySchemaPartVersion,
  getSurveySchemaPart,
  getExtraSurveyPart
} from '../../../api';

import {
  getVariablesWithQuestionResponseOptions,
  getRouteSuffix,
  makeFormCacheKey,
  isSurveySchemaPartType,
  setDocumentTitle
} from '../../../util';

import { publicationStatusValues } from '../../../constants/types';
import {
  COPY_SUFFIX,
  routes,
  EDIT,
  NEW_VERSION,
  COPY,
  CREATE,
  IDENTIFIER
} from '../../../constants';

const compileReturnRoute = (route, params = {}) =>
  route.replace(':surveyPartId', params.surveyPartId || '');

const getSurveyPartActionMode = (pathname, routeBase, type) => {
  const suffix = getRouteSuffix(pathname);

  if (suffix === getRouteSuffix(routeBase.surveyPart.edit.index)) {
    return EDIT;
  }
  if (suffix === getRouteSuffix(routeBase.surveyPart.copy.index)) {
    return COPY;
  }
  if (isSurveySchemaPartType(type) && suffix === getRouteSuffix(routeBase.surveyPart.newVersion.index)) {
    return NEW_VERSION;
  }

  return CREATE;
};

// Custom hook created to reduce complexity in the SurveyPartEditor main component
const useSurveyPartEditor = ({
  surveyPartId,
  surveyPartType,
  location,
  previousLocation,
  translationBase,
  t
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const surveySchemaPart = useSelector(
    (state) => state.surveySchemaPart.surveySchemaPartOptions
  );

  const baseRoute = isSurveySchemaPartType(surveyPartType)
    ? routes.surveySchemas.surveyParts
    : routes.surveySchemas.extraSurveyParts;

  const actionMode = useMemo(
    () => getSurveyPartActionMode(location.pathname, baseRoute, surveyPartType),
    [location.pathname, baseRoute, surveyPartType]
  );

  const previewRoute = `${location.pathname}/preview`;
  const returnedFromPreview = () => {
    if (!previousLocation) return false;
    return previousLocation.pathname.endsWith(previewRoute);
  };

  const [lockedFromEditing, setLockedFromEditing] = useState(false);
  const [codeInputValid, setCodeInputValid] = useState(true);
  const [previousCode, setPreviousCode] = useState('');
  const [formCacheKey, setFormCacheKey] = useState(makeFormCacheKey());
  const [fetchedSurveyPart, setFetchedSurveyPart] = useState(null);
  const [fetchedSurveyPartUsage, setFetchedSurveyPartUsage] = useState(null);

  const initData = useCallback(async () => {
    try {
      dispatch(setTaskPending());

      if ([EDIT, NEW_VERSION, COPY].includes(actionMode)) {
        await fetchExistingSurveyPart();
      } else {
        const updateData = { type: surveyPartType };
        if (!surveySchemaPart.code) {
          const codeResult = await dispatch(
            getOrCheckSurveySchemaPartCodeAction()
          );
          setCodeInputValid(true);
          setPreviousCode(codeResult.value.code);
          updateData.code = codeResult.value.code;
        }
        dispatch(updateSurveySchemaPartOptionsAction(updateData));
      }
    } catch (err) {
      dispatch(addErrorMessageAction(err));
    } finally {
      dispatch(setTaskFulfilled());
    }
  }, [dispatch, actionMode, surveySchemaPart.code, surveyPartType]);

  useEffect(() => {
    initData();
  }, [actionMode, surveyPartId, surveyPartType]);

  const fetchExistingSurveyPart = useCallback(async () => {
    if (!surveyPartId) return;

    const getSurveyPartFn = isSurveySchemaPartType(surveyPartType)
      ? getSurveySchemaPart
      : getExtraSurveyPart;
    const getSurveyPartUsageFn = isSurveySchemaPartType(surveyPartType)
      ? getSurveySchemaPartUsage
      : getExtraSurveyPartUsage;

    const shouldDispatch =
      !surveySchemaPart.id
      || surveySchemaPart.id !== Number(surveyPartId)
      || ([NEW_VERSION, COPY].includes(actionMode) && !returnedFromPreview);

    try {
      const [fetchedPart, fetchedPartUsage] = await Promise.all([
        getSurveyPartFn(surveyPartId),
        getSurveyPartUsageFn(surveyPartId)
      ]);

      if (shouldDispatch) {
        if (isSurveySchemaPartType(surveyPartType)) {
          fetchedPart.variables =
            getVariablesWithQuestionResponseOptions(fetchedPart);
        }
        fetchedPart.isExistingSchemaPart = true;

        if (actionMode === NEW_VERSION) {
          fetchedPart.status = publicationStatusValues.DRAFT;
          fetchedPart.lockedFromEditing = false;
        } else if (actionMode === COPY) {
          fetchedPart.status = publicationStatusValues.DRAFT;
          fetchedPart.lockedFromEditing = false;
          fetchedPart.version = 1;
          fetchedPart.code += COPY_SUFFIX;
        } else if (
          fetchedPart.status === publicationStatusValues.DRAFT
          && fetchedPart.version > 1
        ) {
          const prevSurveyPart = await getSurveySchemaPartVersion(
            surveyPartId,
            fetchedPart.version - 1
          );
          fetchedPart.previousVersionId = prevSurveyPart.id;
        }

        dispatch(updateSurveySchemaPartOptionsAction(fetchedPart));
        setFormCacheKey(makeFormCacheKey());
      }

      setFetchedSurveyPart(fetchedPart);
      setFetchedSurveyPartUsage(fetchedPartUsage);

      setLockedFromEditing(
        fetchedPart.lockedFromEditing && actionMode === EDIT
      );
    } catch (err) {
      dispatch(addErrorMessageAction(err));
    }
  }, [
    actionMode,
    dispatch,
    surveyPartId,
    surveyPartType,
    returnedFromPreview,
    surveySchemaPart.id
  ]);

  const onChange = useCallback(
    (event) => {
      dispatch(
        updateSurveySchemaPartOptionsAction({
          [event.target.name]: event.target.value
        })
      );
    },
    [dispatch]
  );

  const onLocalizedInputChange = useCallback(
    (lang) => (event) => {
      dispatch(
        updateSurveySchemaPartOptionsAction({
          [event.target.name]: { [lang]: event.target.value }
        })
      );
    },
    [dispatch]
  );

  const onCodeInputBlur = useCallback(
    async (event) => {
      let changed = event.target.value.trim();
      // Remove all whitespace from code before checking
      if (changed.indexOf(' ') > -1) {
        changed = changed.replace(/\s/g, '');
        dispatch(
          updateSurveySchemaPartOptionsAction({ [IDENTIFIER]: changed })
        );
      }
      if (changed === '') {
        setCodeInputValid(false);
        setPreviousCode(changed);
      } else if (changed !== previousCode) {
        try {
          const codeResult = await dispatch(
            getOrCheckSurveySchemaPartCodeAction(changed)
          );
          setPreviousCode(changed);
          setCodeInputValid(
            codeResult.value.available && codeResult.value.valid
          );
        } catch (err) {
          dispatch(addErrorMessageAction(err));
        }
      }
    },
    [previousCode, dispatch]
  );

  const cancelEdit = useCallback(() => {
    let returnRoute;
    if (isSurveySchemaPartType(surveyPartType)) {
      returnRoute = surveySchemaPart.id
        ? compileReturnRoute(
          routes.surveySchemas.surveyParts.surveyPart.index,
          { surveyPartId: surveySchemaPart.id }
        )
        : routes.surveySchemas.surveyParts.index;
    } else {
      returnRoute = surveySchemaPart.id
        ? compileReturnRoute(
          routes.surveySchemas.extraSurveyParts.surveyPart.index,
          { surveyPartId: surveySchemaPart.id }
        )
        : routes.surveySchemas.extraSurveyParts.index;
    }

    dispatch(clearSurveySchemaPartOptionsAction());
    navigate(returnRoute);
  }, [surveyPartType, surveySchemaPart, dispatch, navigate]);

  useEffect(() => {
    const titleMap = {
      [NEW_VERSION]: 'newVersionInformationTitle',
      [COPY]: 'newCopyInformationTitle',
      [EDIT]: 'limitedEditingInformationTitle'
    };

    const suffix = titleMap[actionMode] || 'surveyPart';
    setDocumentTitle(t(`${translationBase}.${suffix}`));
  }, [actionMode, t]);

  return {
    surveySchemaPart,
    fetchedSurveyPart,
    fetchedSurveyPartUsage,

    lockedFromEditing,
    codeInputValid,
    formCacheKey,

    actionMode,

    onChange,
    onLocalizedInputChange,
    onCodeInputBlur,
    cancelEdit
  };
};

export default useSurveyPartEditor;
